import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Form, InputGroup, Nav, Row, Table } from "react-bootstrap";
import axios from "../axios";
import Loader from "../components/Loader";
import Message from "../components/Message";
import { AdminFormActions, AdminFormModal, EditIcon, RemoveIcon } from "../components/AdminForm";
import { removePrompt, useConfirm } from "../components/ConfirmDialog";
import { getUserDetails, updateUserProfileDetails } from "../actions/userActions";
import { USER_LOGIN_SUCCESS, USER_UPDATE_PROFILE_RESET } from "../constants/userConstants";

function authConfig(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function errorText(error) {
  return error.response && error.response.data.detail
    ? error.response.data.detail
    : error.message;
}

function carLabel(car) {
  return [car.year, car.manufacture_name, car.model].filter(Boolean).join(" ") || "this car";
}

const sections = [
  { id: "profile", label: "Profile", icon: "fa-user" },
  { id: "login", label: "Login", icon: "fa-key" },
  { id: "garage", label: "Garage", icon: "fa-car" },
  { id: "events", label: "Past events", icon: "fa-clock" },
];

function ProfileScreen() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("");
  const [homePhone, setHomePhone] = useState("");
  const [cellPhone, setCellPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [messageSuccess, setMessageSuccess] = useState("");
  const [accountError, setAccountError] = useState("");
  const [passwordIssues, setPasswordIssues] = useState([]);
  const [accountSuccess, setAccountSuccess] = useState("");
  const [accountLoading, setAccountLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [makes, setMakes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [history, setHistory] = useState([]);
  const [pageError, setPageError] = useState("");
  const [garageError, setGarageError] = useState("");
  const [garageMessage, setGarageMessage] = useState("");
  const [manufacture, setManufacture] = useState("");
  const [vehicleType, setVehicleType] = useState("automobile");
  const [year, setYear] = useState("");
  const [modelName, setModelName] = useState("");
  const [exteriorColor, setExteriorColor] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [condition, setCondition] = useState("");
  const [concours, setConcours] = useState(false);
  const [images, setImages] = useState([]);
  const [editingCar, setEditingCar] = useState(null);
  const [carOpen, setCarOpen] = useState(false);
  const [photoKey, setPhotoKey] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("section");
  const section = sections.some((item) => item.id === requested) ? requested : "profile";
  const userDetails = useSelector((state) => state.userDetails);
  const { loading, error, user } = userDetails;
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const userUpdateProfile = useSelector((state) => state.userUpdateProfile);
  const { success, error: updateError, loading: updateLoading } = userUpdateProfile;

  const loadGarage = async (token) => {
    const config = authConfig(token);
    const [makeData, vehicleData, historyData, countryData] = await Promise.all([
      axios.get("/api/users/manufactures/", config),
      axios.get("/api/users/garage/", config),
      axios.get("/api/users/history/", config),
      axios.get("/api/users/countries/", config),
    ]);
    setMakes(makeData.data);
    setVehicles(vehicleData.data);
    setHistory(historyData.data);
    setCountries(countryData.data);
  };

  useEffect(() => {
    if (!userInfo) {
      navigate("/login");
      return;
    }
    setEmail(userInfo.email || "");
    dispatch(getUserDetails("profile"));
    loadGarage(userInfo.token).catch((err) => setPageError(errorText(err)));
  }, [dispatch, navigate, userInfo]);

  useEffect(() => {
    if (!user?.profile) {
      return;
    }
    setFirstName(user.profile.first_name || "");
    setLastName(user.profile.last_name || "");
    setCompany(user.profile.company || "");
    setAddress(user.profile.address || "");
    setAddress2(user.profile.address2 || "");
    setCity(user.profile.city || "");
    setStateName(user.profile.state || "");
    setZipCode(user.profile.zip_code || "");
    setCountry(user.profile.country ? String(user.profile.country) : "");
    setHomePhone(user.profile.home_phone || "");
    setCellPhone(user.profile.cell_phone || "");
    setEmail(user.email || "");
  }, [user]);

  useEffect(() => {
    if (!success) {
      return;
    }
    setMessageSuccess("Profile saved.");
    dispatch({ type: USER_UPDATE_PROFILE_RESET });
  }, [success, dispatch]);

  const submitHandler = (event) => {
    event.preventDefault();
    setMessageSuccess("");
    setMessageError("");
    dispatch(
      updateUserProfileDetails({
        first_name: firstName,
        last_name: lastName,
        company,
        address,
        address2,
        city,
        state: stateName,
        zip_code: zipCode,
        country: country || "",
        home_phone: homePhone,
        cell_phone: cellPhone,
      })
    );
  };

  const collectPasswordIssues = (nextPassword, nextConfirm) => {
    if (!nextPassword && !nextConfirm) {
      return [];
    }
    const issues = [];
    if (nextPassword.length < 8) {
      issues.push("Password must be at least 8 characters.");
    }
    if (!/[A-Z]/.test(nextPassword)) {
      issues.push("Password must include 1 capital letter.");
    }
    if (!/[0-9]/.test(nextPassword)) {
      issues.push("Password must include 1 number.");
    }
    if (nextPassword !== nextConfirm) {
      issues.push("Passwords do not match.");
    }
    return issues;
  };

  const checkPasswords = (nextPassword, nextConfirm) => {
    setPasswordIssues(collectPasswordIssues(nextPassword, nextConfirm));
  };

  const saveAccountHandler = async (event) => {
    event.preventDefault();
    setAccountSuccess("");
    const issues = collectPasswordIssues(password, confirmPassword);
    setPasswordIssues(issues);
    if (issues.length) {
      return;
    }
    setAccountError("");
    setAccountLoading(true);
    try {
      const { data } = await axios.put(
        "/api/users/account/update/",
        { email, password },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );
      dispatch({ type: USER_LOGIN_SUCCESS, payload: data });
      localStorage.setItem("userInfo", JSON.stringify(data));
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirmPassword(false);
      setPasswordIssues([]);
      setAccountSuccess("Login saved.");
    } catch (err) {
      const detail = err.response && err.response.data.detail;
      if (Array.isArray(detail)) {
        setPasswordIssues(detail);
        setAccountError("");
      } else {
        setAccountError(errorText(err));
      }
    }
    setAccountLoading(false);
  };

  const clearGarageForm = () => {
    setEditingCar(null);
    setManufacture("");
    setVehicleType("automobile");
    setYear("");
    setModelName("");
    setExteriorColor("");
    setLicensePlate("");
    setCondition("");
    setConcours(false);
    setImages([]);
    setPhotoKey((current) => current + 1);
    setCarOpen(false);
  };

  const openAddCar = () => {
    setEditingCar(null);
    setManufacture("");
    setVehicleType("automobile");
    setYear("");
    setModelName("");
    setExteriorColor("");
    setLicensePlate("");
    setCondition("");
    setConcours(false);
    setImages([]);
    setPhotoKey((current) => current + 1);
    setGarageError("");
    setGarageMessage("");
    setCarOpen(true);
  };

  const editCar = (car) => {
    setEditingCar(car);
    setManufacture(String(car.manufacture));
    setVehicleType(car.vehicle_type || "automobile");
    setYear(String(car.year ?? ""));
    setModelName(car.model || "");
    setExteriorColor(car.exterior_color || "");
    setLicensePlate(car.license_plate || "");
    setCondition(car.condition || "");
    setConcours(Boolean(car.concours));
    setImages([]);
    setPhotoKey((current) => current + 1);
    setGarageError("");
    setGarageMessage("");
    setCarOpen(true);
  };

  const addCarHandler = async (event) => {
    event.preventDefault();
    setGarageError("");
    setGarageMessage("");
    if (!manufacture || !modelName.trim() || !year) {
      setGarageError("Choose a make and enter a model year and model.");
      return;
    }
    if (images.length > 5) {
      setGarageError("A vehicle can have at most 5 photos.");
      return;
    }
    const formData = new FormData();
    formData.append("manufacture", manufacture);
    formData.append("vehicle_type", vehicleType);
    formData.append("year", year);
    formData.append("model", modelName);
    formData.append("exterior_color", exteriorColor);
    formData.append("license_plate", licensePlate);
    formData.append("condition", condition);
    formData.append("concours", concours ? "true" : "false");
    images.forEach((file) => formData.append("images", file));
    const editing = editingCar;
    try {
      if (editing) {
        await axios.put(`/api/users/garage/${editing.id}/`, formData, authConfig(userInfo.token));
      } else {
        await axios.post("/api/users/garage/", formData, authConfig(userInfo.token));
      }
      clearGarageForm();
      setGarageMessage(editing ? "Car saved." : "Car added to your garage.");
      const { data } = await axios.get("/api/users/garage/", authConfig(userInfo.token));
      setVehicles(data);
    } catch (err) {
      setGarageError(errorText(err));
    }
  };

  const [ask, confirmDialog] = useConfirm();

  const removeCarHandler = async (car) => {
    const name = [car.year, car.manufacture_name, car.model].filter(Boolean).join(" ") || "this car";
    if (!(await ask(removePrompt(name)))) {
      return;
    }
    setGarageError("");
    try {
      await axios.delete(`/api/users/garage/${car.id}/`, authConfig(userInfo.token));
      if (editingCar?.id === car.id) {
        clearGarageForm();
      }
      setVehicles((current) => current.filter((item) => item.id !== car.id));
    } catch (err) {
      setGarageError(errorText(err));
    }
  };

  return (
    <Row className="dashboard g-4">
      {confirmDialog}
      <Col lg={3}>
        <div className="dashboard-nav">
          <div className="dashboard-user">
            <strong>{userInfo?.name || "Account"}</strong>
            <div className="text-muted small">{userInfo?.email}</div>
          </div>
          <Nav variant="pills" className="flex-column">
            {sections.map((item) => (
              <Nav.Link
                key={item.id}
                active={section === item.id}
                href={`#${item.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  setSearchParams({ section: item.id });
                }}
              >
                <i className={`fa-solid ${item.icon} me-2`}></i>
                {item.label}
              </Nav.Link>
            ))}
          </Nav>
        </div>
      </Col>
      <Col lg={9}>
        <div className="dashboard-panel">
          {pageError && <Message variant="danger">{pageError}</Message>}
          {section === "profile" && (
            <>
              <h2>Profile</h2>
              <p className="text-muted">Contact details for this account.</p>
              {loading && <Loader />}
              {messageSuccess && <Message variant="success">{messageSuccess}</Message>}
              {messageError && <Message variant="danger">{messageError}</Message>}
              {updateError && <Message variant="danger">{updateError}</Message>}
              {error && <Message variant="danger">{error}</Message>}
              <Form onSubmit={submitHandler}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="firstName">
                  <Form.Label>First name</Form.Label>
                  <Form.Control value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="lastName">
                  <Form.Label>Last name</Form.Label>
                  <Form.Control value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3" controlId="company">
              <Form.Label>Company</Form.Label>
              <Form.Control value={company} onChange={(e) => setCompany(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="address">
              <Form.Label>Address</Form.Label>
              <Form.Control value={address} onChange={(e) => setAddress(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="address2">
              <Form.Label>Address 2</Form.Label>
              <Form.Control value={address2} onChange={(e) => setAddress2(e.target.value)} />
            </Form.Group>
            <Row>
              <Col md={5}>
                <Form.Group className="mb-3" controlId="city">
                  <Form.Label>City</Form.Label>
                  <Form.Control value={city} onChange={(e) => setCity(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3" controlId="state">
                  <Form.Label>State</Form.Label>
                  <Form.Control value={stateName} onChange={(e) => setStateName(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="zip">
                  <Form.Label>Zip</Form.Label>
                  <Form.Control value={zipCode} onChange={(e) => setZipCode(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3" controlId="country">
              <Form.Label>Country</Form.Label>
              <Form.Select value={country} onChange={(e) => setCountry(e.target.value)}>
                <option value="">Select a country</option>
                {countries.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="homePhone">
                  <Form.Label>Home phone</Form.Label>
                  <Form.Control value={homePhone} onChange={(e) => setHomePhone(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="cellPhone">
                  <Form.Label>Cell phone</Form.Label>
                  <Form.Control value={cellPhone} onChange={(e) => setCellPhone(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
            <Button type="submit" variant="primary" disabled={updateLoading}>
              Save profile
            </Button>
              </Form>
            </>
          )}

          {section === "login" && (
            <>
              <h2>Login</h2>
              <p className="text-muted">Email and password for signing in. This does not change the profile.</p>
              {accountSuccess && <Message variant="success">{accountSuccess}</Message>}
              {accountError && <Message variant="danger">{accountError}</Message>}
              {passwordIssues.map((issue) => (
                <Message key={issue} variant="danger">
                  {issue}
                </Message>
              ))}
              <Form onSubmit={saveAccountHandler}>
                <Form.Group className="mb-3" controlId="email">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="password">
                  <Form.Label>New password</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="Leave blank to keep the current password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyUp={(e) => checkPasswords(e.target.value, confirmPassword)}
                    />
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => setShowPassword((current) => !current)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    A new password must be at least 8 characters and include 1 capital letter and 1 number.
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="confirmPassword">
                  <Form.Label>Confirm password</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onKeyUp={(e) => checkPasswords(password, e.target.value)}
                    />
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => setShowConfirmPassword((current) => !current)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      <i className={`fa-solid ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                    </Button>
                  </InputGroup>
                </Form.Group>
                <Button type="submit" variant="primary" disabled={accountLoading}>
                  Save login
                </Button>
              </Form>
            </>
          )}

          {section === "garage" && (
            <>
              <div className="garage-head">
                <div>
                  <h2>Garage</h2>
                  <p>Cars you can enter in a show.</p>
                </div>
                <button type="button" className="admin-add" onClick={openAddCar}>
                  Add a car to your garage
                </button>
              </div>
              {garageMessage && <Message variant="success">{garageMessage}</Message>}
              {!carOpen && garageError && <Message variant="danger">{garageError}</Message>}
              {vehicles.length === 0 ? (
                <p className="garage-empty">No cars in the garage yet.</p>
              ) : (
                <ul className="garage-grid">
                  {vehicles.map((car) => {
                    const name = carLabel(car);
                    const photo = car.images?.[0];
                    return (
                      <li key={car.id} className="garage-card">
                        <div className="garage-photo">
                          {photo ? (
                            <img src={photo.display || photo.thumb} alt="" />
                          ) : (
                            <span>No photo</span>
                          )}
                        </div>
                        <strong className="garage-card-name">{name}</strong>
                        <div className="garage-card-actions">
                          <button
                            type="button"
                            className="garage-edit"
                            aria-label={`Edit ${name}`}
                            title={`Edit ${name}`}
                            onClick={() => editCar(car)}
                          >
                            <EditIcon />
                          </button>
                          <button
                            type="button"
                            className="garage-remove"
                            aria-label={`Remove ${name}`}
                            title={`Remove ${name}`}
                            onClick={() => removeCarHandler(car)}
                          >
                            <RemoveIcon />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <AdminFormModal
                show={carOpen}
                size="lg"
                title={editingCar ? `Edit ${carLabel(editingCar)}` : "Add a car"}
                onHide={clearGarageForm}
              >
                {makes.length === 0 && (
                  <Message variant="info">
                    No makes exist yet. An admin needs to add them before a car can be saved.
                  </Message>
                )}
                {garageError && <Message variant="danger">{garageError}</Message>}
                <Form onSubmit={addCarHandler}>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="make">
                        <Form.Label>Make</Form.Label>
                        <Form.Select value={manufacture} onChange={(e) => setManufacture(e.target.value)}>
                          <option value="">Select a make</option>
                          {makes.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.title}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="vehicleType">
                        <Form.Label>Type</Form.Label>
                        <Form.Select value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}>
                          <option value="automobile">Automobile</option>
                          <option value="motorcycle">Motorcycle</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="year">
                        <Form.Label>Model year</Form.Label>
                        <Form.Control type="number" value={year} onChange={(e) => setYear(e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="model">
                        <Form.Label>Model</Form.Label>
                        <Form.Control value={modelName} onChange={(e) => setModelName(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="color">
                        <Form.Label>Exterior color</Form.Label>
                        <Form.Control value={exteriorColor} onChange={(e) => setExteriorColor(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="plate">
                        <Form.Label>License plate</Form.Label>
                        <Form.Control value={licensePlate} onChange={(e) => setLicensePlate(e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="condition">
                        <Form.Label>Condition</Form.Label>
                        <Form.Control value={condition} onChange={(e) => setCondition(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3 make-builds" controlId="concours">
                        <Form.Check
                          type="checkbox"
                          id="concours"
                          label="Concours"
                          checked={concours}
                          onChange={(e) => setConcours(e.target.checked)}
                        />
                        <p className="field-note">This car can enter Concours classes.</p>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3" controlId="photos">
                    <Form.Label>Photos (up to 5)</Form.Label>
                    {editingCar && editingCar.images?.length > 0 && (
                      <div className="garage-thumbs mb-2">
                        {editingCar.images.map((photo) => (
                          <img key={photo.id} src={photo.thumb} alt="" />
                        ))}
                      </div>
                    )}
                    <Form.Control
                      key={photoKey}
                      type="file"
                      accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                      multiple
                      onChange={(e) => setImages(Array.from(e.target.files || []))}
                    />
                    <Form.Text>
                      JPEG or PNG. Cropped from the center to 1000×1000 pixels, expanding or contracting until the last side touches the limit. The 400×400 thumbnail is a resize of that same image.
                    </Form.Text>
                    {editingCar && (
                      <Form.Text className="d-block">
                        If you upload new photos, they replace the current ones.
                      </Form.Text>
                    )}
                  </Form.Group>
                  <AdminFormActions
                    onCancel={clearGarageForm}
                    saveLabel={editingCar ? "Save car" : "Add car"}
                    disabled={makes.length === 0}
                  />
                </Form>
              </AdminFormModal>
            </>
          )}

          {section === "events" && (
            <>
      <h2>Past events</h2>
      {history.length === 0 ? (
        <Message variant="info">No past events yet.</Message>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Event</th>
              <th>Date</th>
              <th>Car</th>
              <th>Class</th>
              <th>Type</th>
              <th>In competition</th>
              <th>Paid</th>
              <th>Trophy</th>
            </tr>
          </thead>
          <tbody>
            {history.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.event_title}</td>
                <td>{entry.event_date}</td>
                <td>{entry.vehicle_name}</td>
                <td>{entry.car_class}</td>
                <td>{entry.division_label}</td>
                <td>{entry.judging ? "Yes" : "No"}</td>
                <td>{entry.paid ? "Yes" : "No"}</td>
                <td>{entry.trophy || ""}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
            </>
          )}
        </div>
      </Col>
    </Row>
  );
}

export default ProfileScreen;
